import { useRef, useEffect, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';

interface AnimalModelProps {
  url: string;
  autoRotate?: boolean;
  rotationSpeed?: number;
}

/**
 * 3D Animal Model component
 * Loads GLB model, auto-centers and scales it to fit the view
 */
export function AnimalModel({
  url,
  autoRotate = true,
  rotationSpeed = 0.005
}: AnimalModelProps) {
  const groupRef = useRef<THREE.Group>(null);
  const { scene } = useGLTF(url);

  // Clone scene once to avoid mutation issues
  const clonedScene = useMemo(() => scene.clone(), [scene]);

  // Center and scale the model on load
  useEffect(() => {
    if (groupRef.current && clonedScene) {
      // Compute bounding box
      const box = new THREE.Box3().setFromObject(clonedScene);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());

      // Scale to fit (target size ~1.5 units for better zoom range)
      const maxDim = Math.max(size.x, size.y, size.z);
      const scale = 1.5 / maxDim;
      clonedScene.scale.setScalar(scale);

      // Center the model
      clonedScene.position.sub(center.multiplyScalar(scale));

      // Clear and add
      groupRef.current.clear();
      groupRef.current.add(clonedScene);
    }
  }, [clonedScene]);

  // Auto-rotation animation
  useFrame(() => {
    if (groupRef.current && autoRotate) {
      groupRef.current.rotation.y += rotationSpeed;
    }
  });

  return <group ref={groupRef} />;
}

// Preload helper
AnimalModel.preload = (url: string) => useGLTF.preload(url);
